import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Database, 
  FileText,
  Settings,
  Activity,
  CheckCircle2,
  ArrowRight,
  Play,
  Pause,
  Layers,
  ShieldCheck,
  Target,
  Award,
  Sparkles,
  Lock,
  Replace
} from 'lucide-react';

interface OPMObject {
  id: string;
  name: string;
  type: 'object' | 'process' | 'state';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  states?: string[];
  currentState?: string;
  processes?: string[];
  connections?: string[];
}

interface OPMProcess {
  id: string;
  name: string;
  type: 'process';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  inputs: string[];
  outputs: string[];
  agents?: string[];
  instruments?: string[];
}

interface OPMAgent {
  id: string;
  name: string;
  type: 'agent';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
}

interface AbstractionLevel {
  level: number;
  name: string;
  description: string;
  objects: OPMObject[];
  processes: OPMProcess[];
  agents: OPMAgent[];
  connections: any[];
  focus: string;
}

const MBSEOPMFlow: React.FC = () => {
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [showOPL, setShowOPL] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['overview']);
  const [companyData, setCompanyData] = useState<{
    company?: string;
    industry?: string;
    objective?: string;
    challenges?: string;
  }>({});

  useEffect(() => {
    // Read company data from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const data = {
      company: urlParams.get('company') || 'Skyflow',
      industry: urlParams.get('industry') || 'Data Privacy & AI Privacy',
      objective: urlParams.get('objective') || 'Pioneering Data Privacy Vaults to securely handle sensitive customer data with a simple API.',
      challenges: urlParams.get('challenges') || 'Intense competition, complex enterprise integration, evolving regulations, and AI privacy complexities.'
    };
    setCompanyData(data);
  }, []);

  // Define abstraction levels following OPM methodology
  const abstractionLevels: AbstractionLevel[] = [
    // L0 - System Level (Highest abstraction)
    {
      level: 0,
      name: "System Level",
      description: "Highest level view showing the complete data privacy ecosystem",
      focus: "Overall system purpose and external interactions",
      objects: [
        {
          id: 'privacy-ecosystem',
          name: 'Data Privacy Ecosystem',
          type: 'object',
          x: 450,
          y: 180,
          width: 280,
          height: 100,
          description: 'Complete end-to-end data privacy and protection system',
          states: ['Inactive', 'Processing', 'Protected'],
          currentState: 'Processing'
        }
      ],
      processes: [
        {
          id: 'protect-data',
          name: 'Protect Sensitive Data',
          type: 'process',
          x: 500,
          y: 320,
          width: 180,
          height: 50,
          description: 'High-level process to secure and govern sensitive data',
          inputs: ['privacy-ecosystem'],
          outputs: ['privacy-ecosystem']
        }
      ],
      agents: [
        {
          id: 'data-stakeholders',
          name: 'Data Stakeholders',
          type: 'agent',
          x: 120,
          y: 180,
          width: 180,
          height: 70,
          description: 'All parties involved in data handling: sources, users, regulators'
        },
        {
          id: 'compliance-framework',
          name: 'Regulatory Framework',
          type: 'agent',
          x: 880,
          y: 180,
          width: 180,
          height: 70,
          description: 'GDPR, HIPAA, PCI-DSS and other regulatory requirements'
        }
      ],
      connections: []
    },
    
    // L1 - Subsystem Level
    {
      level: 1,
      name: "Subsystem Level",
      description: "Major functional subsystems and their interactions",
      focus: "Primary data flow and protection mechanisms",
      objects: [
        {
          id: 'sensitive-data',
          name: 'Sensitive Data',
          type: 'object',
          x: 180,
          y: 200,
          width: 120,
          height: 60,
          description: 'PII, PHI, PCI data requiring protection',
          states: ['Raw', 'Classified', 'Isolated'],
          currentState: 'Raw'
        },
        {
          id: 'vault-system',
          name: 'Privacy Vault System',
          type: 'object',
          x: 450,
          y: 200,
          width: 150,
          height: 60,
          description: 'Central secure storage and processing system',
          states: ['Empty', 'Secured', 'Governed'],
          currentState: 'Empty'
        },
        {
          id: 'privacy-tokens',
          name: 'Privacy Tokens',
          type: 'object',
          x: 720,
          y: 200,
          width: 120,
          height: 60,
          description: 'Non-sensitive format-preserving tokens',
          states: ['Generated', 'Active', 'Expired'],
          currentState: 'Generated'
        },
        {
          id: 'governance-policies',
          name: 'Governance Policies',
          type: 'object',
          x: 450,
          y: 380,
          width: 150,
          height: 60,
          description: 'Access control and compliance rules',
          states: ['Defined', 'Active', 'Updated'],
          currentState: 'Active'
        }
      ],
      processes: [
        {
          id: 'data-ingestion',
          name: 'Ingest & Classify',
          type: 'process',
          x: 315,
          y: 120,
          width: 140,
          height: 45,
          description: 'Discover, classify and prepare sensitive data',
          inputs: ['sensitive-data'],
          outputs: ['vault-system']
        },
        {
          id: 'tokenization',
          name: 'Tokenize & Replace',
          type: 'process',
          x: 585,
          y: 120,
          width: 140,
          height: 45,
          description: 'Generate privacy-preserving tokens',
          inputs: ['vault-system'],
          outputs: ['privacy-tokens']
        },
        {
          id: 'access-control',
          name: 'Govern Access',
          type: 'process',
          x: 585,
          y: 300,
          width: 140,
          height: 45,
          description: 'Control and audit data access',
          inputs: ['privacy-tokens'],
          outputs: [],
          instruments: ['governance-policies']
        }
      ],
      agents: [
        {
          id: 'data-sources',
          name: 'Data Sources',
          type: 'agent',
          x: 80,
          y: 200,
          width: 80,
          height: 60,
          description: 'External systems providing data'
        },
        {
          id: 'data-consumers',
          name: 'Data Consumers',
          type: 'agent',
          x: 960,
          y: 200,
          width: 100,
          height: 60,
          description: 'Applications and users consuming data'
        }
      ],
      connections: []
    },

    // L2 - Component Level  
    {
      level: 2,
      name: "Component Level",
      description: "Detailed components and their specific functions",
      focus: "Individual components and their detailed interactions",
      objects: [
        {
          id: 'pii-data',
          name: 'PII Data',
          type: 'object',
          x: 120,
          y: 120,
          width: 90,
          height: 45,
          description: 'Personally Identifiable Information',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'phi-data',
          name: 'PHI Data',
          type: 'object',
          x: 120,
          y: 180,
          width: 90,
          height: 45,
          description: 'Protected Health Information',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'pci-data',
          name: 'PCI Data',
          type: 'object',
          x: 120,
          y: 240,
          width: 90,
          height: 45,
          description: 'Payment Card Industry Data',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'encryption-engine',
          name: 'Encryption Engine',
          type: 'object',
          x: 350,
          y: 140,
          width: 130,
          height: 50,
          description: 'Polymorphic encryption system',
          states: ['Idle', 'Encrypting', 'Complete'],
          currentState: 'Idle'
        },
        {
          id: 'token-vault',
          name: 'Token Vault',
          type: 'object',
          x: 350,
          y: 220,
          width: 130,
          height: 50,
          description: 'Secure token storage and management',
          states: ['Empty', 'Storing', 'Managing'],
          currentState: 'Empty'
        },
        {
          id: 'access-logs',
          name: 'Access Logs',
          type: 'object',
          x: 350,
          y: 300,
          width: 130,
          height: 50,
          description: 'Audit trail and access records',
          states: ['Logging', 'Stored', 'Archived'],
          currentState: 'Logging'
        },
        {
          id: 'format-tokens',
          name: 'Format-Preserving Tokens',
          type: 'object',
          x: 620,
          y: 140,
          width: 150,
          height: 50,
          description: 'Tokens maintaining original data format',
          states: ['Generated', 'Valid', 'Revoked'],
          currentState: 'Generated'
        },
        {
          id: 'api-keys',
          name: 'API Keys',
          type: 'object',
          x: 620,
          y: 220,
          width: 150,
          height: 50,
          description: 'Authentication and authorization tokens',
          states: ['Active', 'Expired', 'Revoked'],
          currentState: 'Active'
        },
        {
          id: 'policy-rules',
          name: 'Policy Rules',
          type: 'object',
          x: 860,
          y: 200,
          width: 120,
          height: 50,
          description: 'Granular access control rules',
          states: ['Defined', 'Enforced', 'Updated'],
          currentState: 'Enforced'
        }
      ],
      processes: [
        {
          id: 'data-discovery',
          name: 'Discover Data',
          type: 'process',
          x: 250,
          y: 80,
          width: 110,
          height: 35,
          description: 'Scan and identify sensitive data',
          inputs: ['pii-data', 'phi-data', 'pci-data'],
          outputs: ['encryption-engine']
        },
        {
          id: 'classification',
          name: 'Classify Data',
          type: 'process',
          x: 250,
          y: 380,
          width: 110,
          height: 35,
          description: 'Categorize data by sensitivity',
          inputs: ['pii-data', 'phi-data', 'pci-data'],
          outputs: ['encryption-engine']
        },
        {
          id: 'encrypt-data',
          name: 'Encrypt Data',
          type: 'process',
          x: 520,
          y: 80,
          width: 110,
          height: 35,
          description: 'Apply polymorphic encryption',
          inputs: ['encryption-engine'],
          outputs: ['token-vault']
        },
        {
          id: 'generate-tokens',
          name: 'Generate Tokens',
          type: 'process',
          x: 520,
          y: 380,
          width: 110,
          height: 35,
          description: 'Create format-preserving tokens',
          inputs: ['token-vault'],
          outputs: ['format-tokens', 'api-keys']
        },
        {
          id: 'validate-access',
          name: 'Validate Access',
          type: 'process',
          x: 800,
          y: 80,
          width: 110,
          height: 35,
          description: 'Check authorization and log access',
          inputs: ['api-keys'],
          outputs: ['access-logs'],
          instruments: ['policy-rules']
        },
        {
          id: 'audit-compliance',
          name: 'Audit Compliance',
          type: 'process',
          x: 800,
          y: 380,
          width: 110,
          height: 35,
          description: 'Monitor regulatory compliance',
          inputs: ['access-logs'],
          outputs: [],
          instruments: ['policy-rules']
        }
      ],
      agents: [
        {
          id: 'db-systems',
          name: 'Database Systems',
          type: 'agent',
          x: 80,
          y: 60,
          width: 120,
          height: 45,
          description: 'Databases containing sensitive data'
        },
        {
          id: 'api-clients',
          name: 'API Clients',
          type: 'agent',
          x: 80,
          y: 380,
          width: 90,
          height: 45,
          description: 'Applications accessing via API'
        },
        {
          id: 'ai-models',
          name: 'AI/ML Models',
          type: 'agent',
          x: 950,
          y: 80,
          width: 120,
          height: 65,
          description: 'Machine learning systems'
        },
        {
          id: 'end-users',
          name: 'End Users',
          type: 'agent',
          x: 950,
          y: 380,
          width: 90,
          height: 45,
          description: 'Human users of applications'
        }
      ],
      connections: []
    },

    // L3 - Implementation Level (Lowest abstraction)
    {
      level: 3,
      name: "Implementation Level", 
      description: "Detailed implementation specifics and technical components",
      focus: "Technical implementation details and specific algorithms",
      objects: [
        {
          id: 'field-scanner',
          name: 'Field Scanner',
          type: 'object',
          x: 170,
          y: 165,
          width: 100,
          height: 40,
          description: 'Scans database fields for patterns',
          states: ['Scanning', 'Complete'],
          currentState: 'Scanning'
        },
        {
          id: 'regex-patterns',
          name: 'Regex Patterns',
          type: 'object',
          x: 170,
          y: 225,
          width: 100,
          height: 40,
          description: 'Pattern matching rules for data types',
          states: ['Loaded', 'Matching'],
          currentState: 'Loaded'
        },
        {
          id: 'ml-classifier',
          name: 'ML Classifier',
          type: 'object',
          x: 170,
          y: 285,
          width: 100,
          height: 40,
          description: 'Machine learning classification model',
          states: ['Trained', 'Classifying'],
          currentState: 'Trained'
        },
        {
          id: 'aes-256',
          name: 'AES-256 Engine',
          type: 'object',
          x: 360,
          y: 165,
          width: 110,
          height: 40,
          description: 'Advanced Encryption Standard implementation',
          states: ['Ready', 'Encrypting'],
          currentState: 'Ready'
        },
        {
          id: 'key-manager',
          name: 'Key Manager',
          type: 'object',
          x: 360,
          y: 225,
          width: 110,
          height: 40,
          description: 'Cryptographic key lifecycle management',
          states: ['Generating', 'Managing', 'Rotating'],
          currentState: 'Managing'
        },
        {
          id: 'hsm-module',
          name: 'HSM Module',
          type: 'object',
          x: 360,
          y: 285,
          width: 110,
          height: 40,
          description: 'Hardware Security Module for key storage',
          states: ['Secure', 'Processing'],
          currentState: 'Secure'
        },
        {
          id: 'token-generator',
          name: 'Token Generator',
          type: 'object',
          x: 560,
          y: 165,
          width: 110,
          height: 40,
          description: 'Algorithmic token generation system',
          states: ['Ready', 'Generating'],
          currentState: 'Ready'
        },
        {
          id: 'format-validator',
          name: 'Format Validator',
          type: 'object',
          x: 560,
          y: 225,
          width: 110,
          height: 40,
          description: 'Ensures tokens preserve original format',
          states: ['Validating', 'Confirmed'],
          currentState: 'Validating'
        },
        {
          id: 'database-store',
          name: 'Database Store',
          type: 'object',
          x: 560,
          y: 285,
          width: 110,
          height: 40,
          description: 'Persistent storage for tokens and mappings',
          states: ['Storing', 'Indexed'],
          currentState: 'Storing'
        },
        {
          id: 'jwt-tokens',
          name: 'JWT Tokens',
          type: 'object',
          x: 760,
          y: 165,
          width: 100,
          height: 40,
          description: 'JSON Web Tokens for API authentication',
          states: ['Valid', 'Expired'],
          currentState: 'Valid'
        },
        {
          id: 'oauth-handler',
          name: 'OAuth Handler',
          type: 'object',
          x: 760,
          y: 225,
          width: 100,
          height: 40,
          description: 'OAuth 2.0 authorization flow management',
          states: ['Authorized', 'Refreshing'],
          currentState: 'Authorized'
        },
        {
          id: 'rate-limiter',
          name: 'Rate Limiter',
          type: 'object',
          x: 760,
          y: 285,
          width: 100,
          height: 40,
          description: 'API request throttling mechanism',
          states: ['Monitoring', 'Limiting'],
          currentState: 'Monitoring'
        },
        {
          id: 'audit-db',
          name: 'Audit Database',
          type: 'object',
          x: 940,
          y: 225,
          width: 110,
          height: 40,
          description: 'Immutable audit log storage',
          states: ['Recording', 'Archived'],
          currentState: 'Recording'
        }
      ],
      processes: [
        {
          id: 'pattern-match',
          name: 'Pattern Match',
          type: 'process',
          x: 290,
          y: 80,
          width: 100,
          height: 35,
          description: 'Execute regex pattern matching',
          inputs: ['field-scanner', 'regex-patterns', 'ml-classifier'],
          outputs: [],
          agents: ['postgres-db', 'mongodb']
        },
        {
          id: 'ml-inference',
          name: 'ML Inference',
          type: 'process',
          x: 290,
          y: 375,
          width: 100,
          height: 35,
          description: 'Run machine learning classification',
          inputs: ['ml-classifier'],
          outputs: [],
          agents: ['redis-cache']
        },
        {
          id: 'key-derivation',
          name: 'Key Derivation',
          type: 'process',
          x: 490,
          y: 80,
          width: 100,
          height: 35,
          description: 'Generate encryption keys from master key',
          inputs: ['aes-256', 'key-manager', 'hsm-module'],
          outputs: []
        },
        {
          id: 'encrypt-transform',
          name: 'Encrypt Transform',
          type: 'process',
          x: 490,
          y: 375,
          width: 100,
          height: 35,
          description: 'Apply AES encryption transformation',
          inputs: [],
          outputs: []
        },
        {
          id: 'token-mapping',
          name: 'Token Mapping',
          type: 'process',
          x: 690,
          y: 80,
          width: 100,
          height: 35,
          description: 'Create bidirectional token mappings',
          inputs: ['token-generator', 'database-store', 'format-validator'],
          outputs: []
        },
        {
          id: 'format-preserve',
          name: 'Format Preserve',
          type: 'process',
          x: 690,
          y: 375,
          width: 100,
          height: 35,
          description: 'Ensure format preservation in tokens',
          inputs: ['database-store'],
          outputs: []
        },
        {
          id: 'auth-validate',
          name: 'Auth Validate',
          type: 'process',
          x: 880,
          y: 80,
          width: 100,
          height: 35,
          description: 'Validate JWT and OAuth credentials',
          inputs: ['jwt-tokens', 'oauth-handler', 'rate-limiter'],
          outputs: [],
          agents: ['rest-api', 'graphql-api']
        },
        {
          id: 'log-access',
          name: 'Log Access',
          type: 'process',
          x: 880,
          y: 375,
          width: 100,
          height: 35,
          description: 'Record all access attempts and results',
          inputs: ['audit-db'],
          outputs: [],
          agents: ['mobile-app']
        }
      ],
      agents: [
        {
          id: 'postgres-db',
          name: 'PostgreSQL',
          type: 'agent',
          x: 70,
          y: 80,
          width: 90,
          height: 35,
          description: 'PostgreSQL database instance'
        },
        {
          id: 'mongodb',
          name: 'MongoDB',
          type: 'agent',
          x: 70,
          y: 130,
          width: 90,
          height: 35,
          description: 'MongoDB document database'
        },
        {
          id: 'redis-cache',
          name: 'Redis Cache',
          type: 'agent',
          x: 70,
          y: 330,
          width: 90,
          height: 35,
          description: 'Redis in-memory cache'
        },
        {
          id: 'rest-api',
          name: 'REST API',
          type: 'agent',
          x: 1060,
          y: 80,
          width: 90,
          height: 35,
          description: 'RESTful API client'
        },
        {
          id: 'graphql-api',
          name: 'GraphQL API',
          type: 'agent',
          x: 1060,
          y: 130,
          width: 90,
          height: 35,
          description: 'GraphQL API client'
        },
        {
          id: 'mobile-app',
          name: 'Mobile App',
          type: 'agent',
          x: 1060,
          y: 330,
          width: 90,
          height: 35,
          description: 'Mobile application client'
        }
      ],
      connections: []
    }
  ];

  // Get current abstraction level data
  const currentLevel = abstractionLevels[selectedLevel];
  const currentObjects = currentLevel.objects;
  const currentProcesses = currentLevel.processes;
  const currentAgents = currentLevel.agents;

  // Dynamic animation sequences based on abstraction level
  const getAnimationSequence = (level: number) => {
    switch (level) {
      case 0: return [
        { step: 0, description: 'Stakeholders interact with Privacy Ecosystem', objects: ['data-stakeholders', 'privacy-ecosystem'] },
        { step: 1, description: 'System processes and protects sensitive data', objects: ['privacy-ecosystem', 'protect-data'] },
        { step: 2, description: 'Regulatory compliance enforced', objects: ['protect-data', 'compliance-framework'] }
      ];
      case 1: return [
        { step: 0, description: 'Data Sources provide Sensitive Data', objects: ['data-sources', 'sensitive-data'] },
        { step: 1, description: 'Data Ingested & Classified', objects: ['sensitive-data', 'data-ingestion'] },
        { step: 2, description: 'Data secured in Privacy Vault System', objects: ['data-ingestion', 'vault-system'] },
        { step: 3, description: 'Tokens generated and replaced', objects: ['vault-system', 'tokenization', 'privacy-tokens'] },
        { step: 4, description: 'Access governed by policies', objects: ['access-control', 'governance-policies', 'privacy-tokens'] }
      ];
      case 2: return [
        { step: 0, description: 'Multiple data types discovered', objects: ['pii-data', 'phi-data', 'pci-data'] },
        { step: 1, description: 'Data discovery and classification', objects: ['data-discovery', 'classification'] },
        { step: 2, description: 'Encryption engine processes data', objects: ['encrypt-data', 'encryption-engine'] },
        { step: 3, description: 'Tokens generated and validated', objects: ['generate-tokens', 'format-tokens', 'api-keys'] },
        { step: 4, description: 'Access validation and compliance audit', objects: ['validate-access', 'audit-compliance', 'access-logs'] }
      ];
      case 3: return [
        { step: 0, description: 'Field scanning with regex patterns', objects: ['field-scanner', 'regex-patterns', 'pattern-match'] },
        { step: 1, description: 'ML inference for classification', objects: ['ml-classifier', 'ml-inference'] },
        { step: 2, description: 'Key derivation and AES encryption', objects: ['key-derivation', 'aes-256', 'encrypt-transform'] },
        { step: 3, description: 'Token mapping and format preservation', objects: ['token-mapping', 'format-preserve'] },
        { step: 4, description: 'Authentication validation and audit logging', objects: ['auth-validate', 'log-access', 'audit-db'] }
      ];
      default: return [];
    }
  };

  const animationSequence = getAnimationSequence(selectedLevel);

  // Start animation
  const startAnimation = () => {
    setIsAnimating(true);
    setAnimationStep(0);
    
    const interval = setInterval(() => {
      setAnimationStep(prev => {
        if (prev >= animationSequence.length - 1) {
          clearInterval(interval);
          setIsAnimating(false);
          return 0;
        }
        return prev + 1;
      });
    }, 2000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Generate OPL (Object-Process Language) description
  const generateOPL = () => {
    const sentences = [
      `The ${companyData.company} Data Privacy Vault System is designed to handle sensitive data.`,
      "Sensitive Data, which includes PII, PHI, and PCI data, is prone to sprawl if not handled correctly.",
      "The Ingest & Classify process discovers this data and prepares it for isolation.",
      "The Isolate & Protect process moves the data into the Data Privacy Vault and applies polymorphic encryption.",
      "The Data Privacy Vault acts as a zero-trust, centralized, and protected single source of truth.",
      "The Tokenize & Replace process generates non-sensitive, format-preserving Privacy Tokens.",
      "These tokens are used in applications and AI models instead of the original raw data.",
      "The Governance Policy object defines fine-grained rules for access, residency, and compliance.",
      "The Govern & Mediate process uses this policy to handle all access requests, providing de-tokenized views only to authorized users, applications, or AI models.",
      "This system ensures full compliance with major regulations like GDPR, HIPAA, and PCI DSS."
    ];
    
    return sentences.join(' ');
  };

  // Dynamic connections based on abstraction level
  const allElements = [...currentObjects, ...currentProcesses, ...currentAgents];
  const elementMap = new Map(allElements.map(e => [e.id, e]));

  const getAttachmentPoint = (el: any, side: 'left' | 'right' | 'top' | 'bottom' | 'center') => {
    if (!el) return { x: 0, y: 0 };
    switch (side) {
      case 'left': return { x: el.x, y: el.y + el.height / 2 };
      case 'right': return { x: el.x + el.width, y: el.y + el.height / 2 };
      case 'top': return { x: el.x + el.width / 2, y: el.y };
      case 'bottom': return { x: el.x + el.width / 2, y: el.y + el.height };
      default: return { x: el.x + el.width / 2, y: el.y + el.height / 2 };
    }
  };

  const getConnectionsForLevel = (level: number) => {
    const connections: any[] = [];
    
    // Generate connections based on process inputs/outputs and instruments
    currentProcesses.forEach(process => {
      // Input connections (objects to processes) - choose best attachment points
      process.inputs?.forEach(inputId => {
        const inputElement = elementMap.get(inputId);
        if (inputElement) {
          // Determine best connection sides based on relative positions
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (inputElement.x > process.x) {
            startSide = 'left';
            endSide = 'right';
          } else if (Math.abs(inputElement.x - process.x) < 50) {
            if (inputElement.y < process.y) {
              startSide = 'bottom';
              endSide = 'top';
            } else {
              startSide = 'top';
              endSide = 'bottom';
            }
          }
          
          connections.push({
            start: getAttachmentPoint(inputElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerEnd: 'url(#arrowhead)',
            type: 'input'
          });
        }
      });

      // Output connections (processes to objects)
      process.outputs?.forEach(outputId => {
        const outputElement = elementMap.get(outputId);
        if (outputElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (outputElement.x < process.x) {
            startSide = 'left';
            endSide = 'right';
          } else if (Math.abs(outputElement.x - process.x) < 50) {
            if (outputElement.y > process.y) {
              startSide = 'bottom';
              endSide = 'top';
            } else {
              startSide = 'top';
              endSide = 'bottom';
            }
          }
          
          connections.push({
            start: getAttachmentPoint(process, startSide),
            end: getAttachmentPoint(outputElement, endSide),
            markerEnd: 'url(#arrowhead)',
            type: 'output'
          });
        }
      });

      // Instrument connections (from bottom of instrument to process)
      process.instruments?.forEach(instrumentId => {
        const instrumentElement = elementMap.get(instrumentId);
        if (instrumentElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'top';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'bottom';
          
          if (process.y > instrumentElement.y) {
            startSide = 'bottom';
            endSide = 'top';
          }
          
          connections.push({
            start: getAttachmentPoint(instrumentElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerStart: 'url(#instrument-arrow)',
            markerEnd: 'url(#arrowhead)',
            type: 'instrument'
          });
        }
      });

      // Agent connections (to processes they trigger)
      process.agents?.forEach(agentId => {
        const agentElement = elementMap.get(agentId);
        if (agentElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (agentElement.x > process.x) {
            startSide = 'left';
            endSide = 'right';
          }
          
          connections.push({
            start: getAttachmentPoint(agentElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerStart: 'url(#agent-arrow)',
            markerEnd: 'url(#arrowhead)',
            type: 'agent'
          });
        }
      });
    });

    return connections;
  };

  const connections = getConnectionsForLevel(selectedLevel);

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 100, 0],
              y: [0, -50, 0],
              rotate: [0, 180, 360]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 left-20 w-32 h-32 rounded-full blur-xl" 
            style={{ background: 'linear-gradient(90deg, rgba(66, 102, 83, 0.2), rgba(13, 86, 48, 0.2))' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl sqsft-heading-1 mb-6 leading-tight"
            >
              MBSE OPM Flow
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl md:text-3xl sqsft-body max-w-4xl mx-auto mb-8 font-light leading-relaxed" 
              style={{ color: 'rgb(var(--color-text-secondary))' }}
            >
              A Model-Based Systems Engineering view of the{' '}
              <span className="sqsft-gradient-text font-semibold">
                {companyData.company} Data Privacy Vault
              </span>{' '}
              using Object-Process Methodology.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-6 mb-12"
            >
              {[
                { icon: Shield, text: "Zero-Trust Vault", color: "sqsft-gradient-primary" },
                { icon: Lock, text: "Polymorphic Encryption", color: "sqsft-gradient-primary" },
                { icon: Replace, text: "Data Tokenization", color: "sqsft-gradient-primary" },
                { icon: Award, text: "Global Compliance", color: "sqsft-gradient-primary" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center sqsft-card backdrop-blur-xl px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" 
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center mr-3`}>
                    <item.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm sqsft-body font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-8 pb-20">
        {/* Breadcrumb Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-2xl p-4 border border-white/50 dark:border-slate-700/50 shadow-lg">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-medium">1. Roadmap Input</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                <div className="w-4 h-4 rounded-full bg-blue-600 dark:bg-blue-400 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                </div>
                <span className="font-semibold">2. Systems Analysis</span>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
              <div className="flex items-center space-x-2 text-gray-400">
                <div className="w-4 h-4 rounded-full border-2 border-gray-400"></div>
                <span>3. Innovation Workflow</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Control Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-white/50 dark:border-slate-700/50 shadow-2xl">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Layers className="w-5 h-5" style={{ color: 'rgb(var(--color-sage-green))' }} />
                  <span className="font-semibold text-gray-700 dark:text-gray-300">Abstraction Level:</span>
                </div>
                <div className="flex space-x-2">
                  {abstractionLevels.map((levelData, index) => (
                    <div key={index} className="relative group">
                      <button
                        onClick={() => setSelectedLevel(index)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                          selectedLevel === index
                            ? 'sqsft-btn-primary text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                        }`}
                      >
                        L{index}: {levelData.name}
                      </button>
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                        {levelData.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setShowOPL(!showOPL)}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  <span>OPL</span>
                </button>
                
                <button
                  onClick={startAnimation}
                  disabled={isAnimating}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors disabled:opacity-50"
                >
                  {isAnimating ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  <span>{isAnimating ? 'Workflow' : 'Workflow'}</span>
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Level Description Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-2xl p-6 border border-blue-200 dark:border-blue-700">
            <div className="flex items-center mb-3">
              <Target className="w-5 h-5 mr-2" style={{ color: 'rgb(var(--color-sage-green))' }} />
              <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                {currentLevel.name} (L{selectedLevel})
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-2">{currentLevel.description}</p>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Focus:</strong> {currentLevel.focus}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs">
                {currentObjects.length} Objects
              </span>
              <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded text-xs">
                {currentProcesses.length} Processes
              </span>
              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded text-xs">
                {currentAgents.length} Agents
              </span>
            </div>
          </div>
        </motion.div>

        {/* OPL Text Display */}
        <AnimatePresence>
          {showOPL && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl p-6 border border-slate-200 dark:border-slate-700">
                <div className="flex items-center mb-4">
                  <FileText className="w-5 h-5 mr-2" style={{ color: 'rgb(var(--color-sage-green))' }} />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                    Object-Process Language (OPL) Description
                  </h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                  {generateOPL()}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main OPM Diagram */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-8 border border-white/50 dark:border-slate-700/50 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                {companyData.company} Data Protection Flow - {currentLevel.name}
              </h2>
              {isAnimating && (
                <div className="flex items-center space-x-2 text-sm" style={{ color: 'rgb(var(--color-sage-green))' }}>
                  <Activity className="w-4 h-4 animate-pulse" />
                  <span>{animationSequence[animationStep]?.description}</span>
                </div>
              )}
            </div>

            {/* SVG Diagram */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 overflow-x-auto">
              <svg width="1200" height="480" viewBox="0 0 1200 480" className="w-full h-auto">
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="rgb(var(--color-sage-green))" />
                  </marker>
                  <marker id="agent-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <circle cx="4" cy="4" r="4" fill="black" />
                  </marker>
                  <marker id="instrument-arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto" markerUnits="userSpaceOnUse">
                    <circle cx="4" cy="4" r="4" fill="white" stroke="rgb(139, 69, 19)" strokeWidth="1.5" />
                  </marker>
                </defs>

                {/* System Boundary */}
                <rect x="60" y="50" width="1080" height="410" rx="20" fill="rgba(245, 245, 245, 0.7)" stroke="#ccc" strokeDasharray="5,5" />
                <text x="600" y="40" textAnchor="middle" className="text-lg font-semibold fill-gray-600 dark:fill-gray-300">
                  {companyData.company} {currentLevel.name} - L{selectedLevel}
                </text>

                {/* Agents */}
                {currentAgents.map((agent) => (
                  <g key={agent.id} onClick={() => setSelectedObject(agent.id)}>
                    <motion.rect
                      x={agent.x}
                      y={agent.y}
                      width={agent.width}
                      height={agent.height}
                      rx="8"
                      fill={
                        isAnimating && animationSequence[animationStep]?.objects.includes(agent.id)
                          ? 'rgba(252, 211, 77, 0.6)'
                          : 'rgba(252, 211, 77, 0.2)'
                      }
                      stroke="rgb(var(--color-royal-gold))"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-300"
                       animate={{
                        scale: isAnimating && animationSequence[animationStep]?.objects.includes(agent.id) ? 1.05 : 1,
                        strokeWidth: isAnimating && animationSequence[animationStep]?.objects.includes(agent.id) ? 3 : 2
                      }}
                      transition={{ duration: 0.3 }}
                    />
                     <text
                      x={agent.x + agent.width / 2}
                      y={agent.y + agent.height / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-sm font-medium fill-gray-800 dark:fill-gray-200 pointer-events-none"
                      style={{ whiteSpace: 'pre-line' }}
                    >
                      {agent.name.split('/').map((line, i) => <tspan key={i} x={agent.x + agent.width/2} dy={i === 0 ? 0 : "1.2em"}>{line.trim()}</tspan>)}
                    </text>
                  </g>
                ))}

                {/* Objects (Rectangles) */}
                {currentObjects.map((obj) => (
                  <g key={obj.id}>
                    <motion.rect
                      x={obj.x}
                      y={obj.y}
                      width={obj.width}
                      height={obj.height}
                      fill={
                        isAnimating && animationSequence[animationStep]?.objects.includes(obj.id)
                          ? 'rgba(66, 102, 83, 0.8)'
                          : 'rgba(66, 102, 83, 0.3)'
                      }
                      stroke="rgb(var(--color-sage-green))"
                      strokeWidth="2"
                      rx="8"
                      className="cursor-pointer transition-all duration-300"
                      onClick={() => setSelectedObject(obj.id)}
                      animate={{
                        scale: isAnimating && animationSequence[animationStep]?.objects.includes(obj.id) ? 1.1 : 1,
                        strokeWidth: isAnimating && animationSequence[animationStep]?.objects.includes(obj.id) ? 3 : 2
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    <text
                      x={obj.x + obj.width / 2}
                      y={obj.y + obj.height / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-sm font-medium fill-gray-800 dark:fill-gray-200"
                    >
                      {obj.name}
                    </text>
                    {obj.currentState && (
                      <text
                        x={obj.x + obj.width / 2}
                        y={obj.y + obj.height / 2 + 15}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-xs fill-gray-600 dark:fill-gray-400"
                      >
                        [{obj.currentState || (obj.states && obj.states[0])}]
                      </text>
                    )}
                  </g>
                ))}

                {/* Processes (Ellipses) */}
                {currentProcesses.map((process) => (
                  <g key={process.id}>
                    <motion.ellipse
                      cx={process.x + process.width / 2}
                      cy={process.y + process.height / 2}
                      rx={process.width / 2}
                      ry={process.height / 2}
                      fill={
                        isAnimating && animationSequence[animationStep]?.objects.includes(process.id)
                          ? 'rgba(59, 130, 246, 0.8)'
                          : 'rgba(59, 130, 246, 0.3)'
                      }
                      stroke="rgb(59, 130, 246)"
                      strokeWidth="2"
                      className="cursor-pointer transition-all duration-300"
                      onClick={() => setSelectedObject(process.id)}
                      animate={{
                        scale: isAnimating && animationSequence[animationStep]?.objects.includes(process.id) ? 1.1 : 1,
                        strokeWidth: isAnimating && animationSequence[animationStep]?.objects.includes(process.id) ? 3 : 2
                      }}
                      transition={{ duration: 0.3 }}
                    />
                    <text
                      x={process.x + process.width / 2}
                      y={process.y + process.height / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      className="text-sm font-medium fill-gray-800 dark:fill-gray-200 pointer-events-none"
                    >
                      {process.name}
                    </text>
                  </g>
                ))}

                {/* Connections (Arrows) */}
                {connections.map((conn, i) => (
                   <line 
                    key={i}
                    x1={conn.start.x} y1={conn.start.y} 
                    x2={conn.end.x} y2={conn.end.y} 
                    stroke={
                      conn.type === 'agent' ? 'rgb(252, 211, 77)' :
                      conn.type === 'instrument' ? 'rgb(139, 69, 19)' :
                      'rgb(var(--color-sage-green))'
                    }
                    strokeWidth={
                      isAnimating && (
                        animationSequence[animationStep]?.objects.some(objId => 
                          objId === elementMap.get(conn.start.id)?.id || 
                          objId === elementMap.get(conn.end.id)?.id
                        )
                      ) ? "3" : "2"
                    }
                    strokeDasharray={conn.type === 'instrument' ? '5,5' : 'none'}
                    markerEnd={conn.type === 'instrument' ? '' : conn.markerEnd}
                    markerStart={conn.markerStart}
                    className="transition-all duration-300"
                  />
                ))}
              </svg>
            </div>
          </div>
        </motion.div>

        {/* Selected Component Details */}
        {selectedObject && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-white/50 dark:border-slate-700/50 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Component Details</h3>
                <button 
                  onClick={() => setSelectedObject(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>
              {(() => {
                const selectedComponent = [...currentObjects, ...currentProcesses, ...currentAgents].find(c => c.id === selectedObject);
                if (!selectedComponent) return null;
                
                return (
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-6 h-6 ${
                        selectedComponent.type === 'object' ? 'bg-green-500 rounded' :
                        selectedComponent.type === 'process' ? 'bg-blue-500 rounded-full' :
                        'bg-yellow-500 rounded'
                      }`}></div>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">{selectedComponent.name}</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          selectedComponent.type === 'process' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                          selectedComponent.type === 'agent' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        }`}>
                          {selectedComponent.type}
                        </span>
                      </div>
                    </div>
                    
                    <p className="text-gray-700 dark:text-gray-300">{selectedComponent.description}</p>
                    
                    {selectedComponent.type === 'object' && (selectedComponent as OPMObject).states && (
                      <div>
                        <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">States:</h5>
                        <div className="flex flex-wrap gap-2">
                          {(selectedComponent as OPMObject).states!.map(state => (
                            <span key={state} className={`px-2 py-1 rounded text-xs ${
                              state === (selectedComponent as OPMObject).currentState 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}>
                              {state}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {selectedComponent.type === 'process' && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(selectedComponent as OPMProcess).inputs && (selectedComponent as OPMProcess).inputs.length > 0 && (
                          <div>
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Inputs:</h5>
                            <div className="space-y-1">
                              {(selectedComponent as OPMProcess).inputs.map(input => (
                                <span key={input} className="block text-sm text-gray-600 dark:text-gray-400">• {input}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {(selectedComponent as OPMProcess).outputs && (selectedComponent as OPMProcess).outputs.length > 0 && (
                          <div>
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Outputs:</h5>
                            <div className="space-y-1">
                              {(selectedComponent as OPMProcess).outputs.map(output => (
                                <span key={output} className="block text-sm text-gray-600 dark:text-gray-400">• {output}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </motion.div>
        )}

        {/* System Details and Compliance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* System Components */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-white/50 dark:border-slate-700/50 shadow-2xl h-full">
              <div className="flex items-center mb-6">
                <Settings className="w-6 h-6 mr-3" style={{ color: 'rgb(var(--color-sage-green))' }} />
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">System Components</h3>
              </div>
              
              {/* Component Categories */}
              <div className="space-y-6">
                {/* Objects Section */}
                {currentObjects.length > 0 && (
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-4 h-4 rounded bg-green-500 mr-2"></div>
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        Objects ({currentObjects.length})
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {currentObjects.map((obj) => (
                        <div key={obj.id} className="group relative">
                          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3 hover:shadow-md transition-all duration-200 cursor-pointer"
                               onClick={() => setSelectedObject(obj.id)}>
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="font-medium text-gray-800 dark:text-gray-200 text-sm">{obj.name}</h5>
                              {obj.currentState && (
                                <span className="px-2 py-1 bg-green-100 dark:bg-green-800 text-green-700 dark:text-green-200 rounded text-xs">
                                  {obj.currentState}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{obj.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Processes Section */}
                {currentProcesses.length > 0 && (
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-4 h-4 rounded-full bg-blue-500 mr-2"></div>
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        Processes ({currentProcesses.length})
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {currentProcesses.map((process) => (
                        <div key={process.id} className="group relative">
                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 hover:shadow-md transition-all duration-200 cursor-pointer"
                               onClick={() => setSelectedObject(process.id)}>
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="font-medium text-gray-800 dark:text-gray-200 text-sm">{process.name}</h5>
                              <div className="flex space-x-1">
                                {process.inputs && process.inputs.length > 0 && (
                                  <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded text-xs">
                                    {process.inputs.length} in
                                  </span>
                                )}
                                {process.outputs && process.outputs.length > 0 && (
                                  <span className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded text-xs">
                                    {process.outputs.length} out
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{process.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Agents Section */}
                {currentAgents.length > 0 && (
                  <div>
                    <div className="flex items-center mb-3">
                      <div className="w-4 h-4 rounded bg-yellow-500 mr-2"></div>
                      <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
                        Agents ({currentAgents.length})
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {currentAgents.map((agent) => (
                        <div key={agent.id} className="group relative">
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3 hover:shadow-md transition-all duration-200 cursor-pointer"
                               onClick={() => setSelectedObject(agent.id)}>
                            <h5 className="font-medium text-gray-800 dark:text-gray-200 text-sm mb-1">{agent.name}</h5>
                            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{agent.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
          
          {/* Level Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-white/90 dark:bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-white/50 dark:border-slate-700/50 shadow-2xl h-full">
              <div className="flex items-center mb-4">
                <Database className="w-6 h-6 mr-3" style={{ color: 'rgb(var(--color-sage-green))' }} />
                <h3 className="text-xl font-bold text-gray-800 dark:text-gray-200">Level Summary</h3>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-blue-50 to-green-50 dark:from-blue-900/20 dark:to-green-900/20 rounded-lg p-4">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Level</div>
                  <div className="text-2xl font-bold text-gray-800 dark:text-gray-200 mb-1">
                    L{selectedLevel}: {currentLevel.name}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{currentLevel.focus}</div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{currentObjects.length}</div>
                    <div className="text-xs text-gray-500">Objects</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{currentProcesses.length}</div>
                    <div className="text-xs text-gray-500">Processes</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">{currentAgents.length}</div>
                    <div className="text-xs text-gray-500">Agents</div>
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 border-t border-gray-200 dark:border-gray-700 pt-3">
                  <strong>OPM Methodology:</strong> Following ISO 19450:2024 standards for Object-Process Modeling with hierarchical abstraction and refinement mechanisms.
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Business Model Integration Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-center"
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              const params = new URLSearchParams({
                autostart: 'true',
                company: companyData.company || 'Skyflow Inc.',
                industry: companyData.industry || 'Data Privacy & Security',
                objective: companyData.objective || '',
                challenges: companyData.challenges || ''
              });
              window.location.href = `/business-model?${params.toString()}`;
            }}
            className="group relative px-16 py-6 rounded-3xl font-bold text-xl transition-all duration-300 flex items-center mx-auto shadow-2xl sqsft-btn-primary text-white"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <Sparkles className="w-7 h-7 mr-4 group-hover:animate-pulse" />
            Start Innovation Workflow
            <ArrowRight className="w-7 h-7 ml-4 group-hover:translate-x-2 transition-transform duration-300" />
          </motion.button>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-gray-600 dark:text-gray-400"
          >
            ⚡ Begin your AI-powered TRIZ innovation workflow
          </motion.p>
        </motion.div>
      </div>
    </div>
  );
};

export default MBSEOPMFlow;